import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "./config";

const MessageThread = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [otherUser, setOtherUser] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const messagesEndRef = useRef(null);
  const messageContainerRef = useRef(null);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE_URL}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentUserId(res.data._id || res.data.id);
      setCurrentUserProfile({
        _id: res.data._id || res.data.id,
        profilePic: res.data.profilePic,
        displayUsername: res.data.displayUsername,
        username: res.data.username
      });
    } catch (err) {
      console.error("Error fetching current user:", err);
    }
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      const res = await axios.get(`${API_BASE_URL}/api/messages/conversation/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessages(res.data.messages);
      setOtherUser(res.data.otherUser);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching messages:", err);
      if (err.response?.status === 404) {
        setError("User not found");
      } else {
        setError("Failed to load messages");
      }
      setLoading(false);
    }
  }, [userId, navigate]);

  useEffect(() => {
    fetchCurrentUser();
    fetchMessages();
    // Refresh messages every 10 seconds
    const interval = setInterval(fetchMessages, 10000);
    return () => clearInterval(interval);
  }, [fetchCurrentUser, fetchMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/api/messages`,
        {
          recipientId: userId,
          content: newMessage
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Don't override the sender, just use the response as is
      // The server should return the properly populated message
      setNewMessage("");
      setSending(false);

      // Refresh messages to get the properly populated version
      fetchMessages();
    } catch (err) {
      console.error("Error sending message:", err);
      setError(err.response?.data?.error || "Failed to send message");
      setSending(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return `Yesterday ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString([], { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error === "User not found") {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          User not found
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="card" style={{ height: "calc(100vh - 120px)" }}>
            {/* Header */}
            <div className="card-header bg-primary text-white">
              <div className="d-flex align-items-center">
                <Link to="/messages" className="btn btn-sm btn-light me-3">
                  <i className="bi bi-arrow-left"></i> Back
                </Link>
                {otherUser && (
                  <>
                    <img
                      src={otherUser.profilePic ? 
                        (otherUser.profilePic.startsWith('http') 
                          ? otherUser.profilePic 
                          : `${API_BASE_URL}${otherUser.profilePic.startsWith('/') ? '' : '/'}${otherUser.profilePic}`)
                        : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                          otherUser.displayUsername || otherUser.username || 'User'
                        )}&background=ffffff&color=007bff&size=40`}
                      alt={otherUser.displayUsername || otherUser.username}
                      className="rounded-circle me-2"
                      style={{ width: "40px", height: "40px", objectFit: "cover" }}
                      onError={(e) => {
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                          otherUser.displayUsername || otherUser.username || 'User'
                        )}&background=ffffff&color=007bff&size=40`;
                      }}
                    />
                    <div>
                      <h5 className="mb-0 text-white">
                        {otherUser.displayUsername || otherUser.username}
                      </h5>
                      {otherUser.fullName && (
                        <small className="text-white opacity-75">{otherUser.fullName}</small>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Messages */}
            <div 
              className="card-body overflow-auto"
              ref={messageContainerRef}
              style={{ backgroundColor: "#f8f9fa" }}
            >
              {error && (
                <div className="alert alert-danger mb-3">{error}</div>
              )}

              {messages.length === 0 ? (
                <div className="text-center p-5">
                  <p className="text-muted">No messages yet. Start the conversation!</p>
                </div>
              ) : (
                <div className="messages-container">
                  {messages.map((message, index) => {
                    // Get sender information
                    const senderId = message.sender?._id || message.sender?.id || message.sender;
                    const senderUsername = message.sender?.username;
                    const senderDisplayUsername = message.sender?.displayUsername;
                    
                    // Try multiple comparison methods
                    const idComparison = currentUserId && (
                      String(senderId) === String(currentUserId) ||
                      senderId === currentUserId ||
                      message.sender?._id === currentUserId ||
                      message.sender?.id === currentUserId
                    );
                    
                    // Username fallback comparison
                    const usernameComparison = currentUserProfile && (
                      senderUsername === currentUserProfile.username ||
                      senderDisplayUsername === currentUserProfile.displayUsername
                    );
                    
                    // Final determination
                    const isCurrentUser = idComparison || usernameComparison;
                    
                    const showDate = index === 0 || 
                      new Date(messages[index - 1].createdAt).toDateString() !== 
                      new Date(message.createdAt).toDateString();

                    return (
                      <React.Fragment key={message._id}>
                        {showDate && (
                          <div className="text-center my-3">
                            <small className="text-muted bg-white px-3 py-1 rounded">
                              {new Date(message.createdAt).toLocaleDateString([], {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </small>
                          </div>
                        )}
                        
                        <div 
                          style={{
                            display: 'flex',
                            justifyContent: isCurrentUser ? 'flex-end' : 'flex-start',
                            marginBottom: '1rem',
                            alignItems: 'end'
                          }}
                        >
                          {!isCurrentUser && (
                            <img
                              src={message.sender.profilePic ? 
                                (message.sender.profilePic.startsWith('http') 
                                  ? message.sender.profilePic 
                                  : `${API_BASE_URL}${message.sender.profilePic.startsWith('/') ? '' : '/'}${message.sender.profilePic}`)
                                : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                  message.sender.displayUsername || message.sender.username || 'User'
                                )}&background=6c757d&color=fff&size=40`}
                              alt={message.sender.displayUsername || message.sender.username}
                              className="rounded-circle"
                              style={{ 
                                width: "40px", 
                                height: "40px", 
                                objectFit: "cover",
                                marginRight: "8px",
                                flexShrink: 0
                              }}
                              onError={(e) => {
                                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                  message.sender.displayUsername || message.sender.username || 'User'
                                )}&background=6c757d&color=fff&size=40`;
                              }}
                            />
                          )}
                          <div
                            style={{ 
                              maxWidth: "70%",
                              padding: "12px 16px",
                              borderRadius: isCurrentUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                              backgroundColor: isCurrentUser ? "#007bff" : "#e9ecef",
                              color: isCurrentUser ? "white" : "black",
                              wordWrap: "break-word"
                            }}
                          >
                            {!isCurrentUser && (
                              <small style={{ 
                                display: "block", 
                                fontWeight: "bold", 
                                marginBottom: "4px",
                                color: "#495057"
                              }}>
                                {message.sender.displayUsername || message.sender.username}
                              </small>
                            )}
                            <div style={{ marginBottom: "4px" }}>{message.content}</div>
                            <small style={{ 
                              opacity: 0.7,
                              fontSize: "0.75rem"
                            }}>
                              {formatTime(message.createdAt)}
                            </small>
                          </div>
                          {isCurrentUser && currentUserProfile && (
                            <img
                              src={currentUserProfile.profilePic ? 
                                (currentUserProfile.profilePic.startsWith('http') 
                                  ? currentUserProfile.profilePic 
                                  : `${API_BASE_URL}${currentUserProfile.profilePic.startsWith('/') ? '' : '/'}${currentUserProfile.profilePic}`)
                                : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                  currentUserProfile.displayUsername || currentUserProfile.username || 'Me'
                                )}&background=007bff&color=fff&size=40`}
                              alt="You"
                              className="rounded-circle"
                              style={{ 
                                width: "40px", 
                                height: "40px", 
                                objectFit: "cover",
                                marginLeft: "8px",
                                flexShrink: 0
                              }}
                              onError={(e) => {
                                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                  currentUserProfile.displayUsername || currentUserProfile.username || 'Me'
                                )}&background=007bff&color=fff&size=40`;
                              }}
                            />
                          )}
                        </div>
                      </React.Fragment>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Message Input */}
            <div className="card-footer">
              <form onSubmit={sendMessage}>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    disabled={sending}
                    maxLength={500}
                  />
                  <button 
                    className="btn btn-primary" 
                    type="submit"
                    disabled={!newMessage.trim() || sending}
                  >
                    {sending ? (
                      <span className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Sending...</span>
                      </span>
                    ) : (
                      "Send"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageThread;